import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  CheckCircle2,
  Sparkles,
  Pencil,
} from 'lucide-react';
import { lactanciaSteps } from '../lactanciaForm';
import { profile } from '../mock';

const STORAGE_KEY = 'kn-lactancia-form';

const LactanciaPreConsulta = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [restoredNotice, setRestoredNotice] = useState(false);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          setAnswers(parsed);
          setRestoredNotice(true);
          setTimeout(() => setRestoredNotice(false), 4500);
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
    } catch (e) {
      /* ignore */
    }
  }, [answers]);

  const totalSteps = lactanciaSteps.length;
  const step = lactanciaSteps[stepIdx];
  const progress = Math.round(((stepIdx + (showSummary ? 1 : 0)) / totalSteps) * 100);

  const setField = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const validateStep = () => {
    if (!step.fields) return true;
    for (const f of step.fields) {
      if (f.required && !(answers[f.key] || '').trim()) return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    if (stepIdx < totalSteps - 1) {
      setStepIdx(stepIdx + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setShowSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrev = () => {
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    if (stepIdx > 0) {
      setStepIdx(stepIdx - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const editStep = (idx) => {
    setShowSummary(false);
    setStepIdx(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const restartForm = () => {
    if (!window.confirm('¿Borrar todo y empezar de nuevo?')) return;
    setAnswers({});
    setStepIdx(0);
    setShowSummary(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  const whatsappText = useMemo(() => {
    const a = answers;
    const line = (label, val) => (val && val.trim() ? `• ${label}: ${val.trim()}\n` : '');
    let msg = '';
    msg += '🌿 *Pre-consulta de lactancia* — Karina Negrín\n\n';
    msg += `*Mamá:* ${a.nombre || '—'}${a.edadMama ? `, ${a.edadMama} años` : ''}\n`;
    msg += `*Bebé:* ${a.momentoBebe || '—'}\n\n`;

    msg += '*📍 EMBARAZO*\n';
    msg += line('Cómo fue', a.embarazoComplicaciones);
    msg += line('Controles e indicaciones', a.controlesIndicaciones);
    msg += line('Preparación previa', a.preparacionLactancia);

    msg += '\n*📍 NACIMIENTO Y PRIMEROS MOMENTOS*\n';
    msg += line('Tipo de parto', a.tipoParto);
    msg += line('Fecha de nacimiento / FPP', a.duracionParto);
    msg += line('Cómo se sintió', a.comoSentiste);
    msg += line('Piel con piel', a.pielConPiel);
    msg += line('Primera toma 1ª hora', a.primeraToma);
    msg += line('Complicaciones neonatales', a.complicacionesNeonatales);

    msg += '\n*📍 SOBRE EL BEBÉ*\n';
    msg += line('A término', a.aTermino);
    msg += line('Peso al nacer', a.pesoNacer);
    msg += line('Peso al alta', a.pesoAlta);
    msg += line('Peso hoy', a.pesoHoy);
    msg += line('Suplementos', a.suplementos);

    msg += '\n*📍 LACTANCIA HOY*\n';
    msg += line('Estado', a.estaAmamantando);
    msg += line('Frecuencia día', a.frecuenciaDia);
    msg += line('Frecuencia noche', a.frecuenciaNoche);
    msg += line('Duración tomas', a.duracionTomas);
    msg += line('Pechos por toma', a.ofreceAmbosPechos);
    msg += line('Pañales', a.panales);
    msg += line('Chupete / mamadera', a.chupeteMamadera);

    msg += '\n*📍 DIFICULTADES*\n';
    msg += line('Dolor', a.hayDolor);
    msg += line('Cómo es el dolor', a.describirDolor);
    msg += line('Pezones', a.pezones);
    msg += line('Ingurgitación / mastitis', a.ingurgitacionMastitis);

    msg += '\n*📍 HISTORIA Y CUERPO*\n';
    msg += line('Lactancias previas', a.lactanciasPrevias);
    msg += line('Condiciones médicas', a.condicionesMedicas);
    msg += line('Medicación', a.medicacion);
    msg += line('Cirugías mamarias', a.cirugias);

    msg += '\n*📍 LO QUE SIENTE Y QUIERE*\n';
    msg += line('Estado emocional', a.estadoEmocional);
    msg += line('Objetivo', a.objetivoLactancia);
    msg += line('Foco del encuentro', a.focoEncuentro);

    msg += '\n— Enviado desde karinanegrin.com 🌿';
    return msg;
  }, [answers]);

  const whatsappLink = `${profile.whatsapp}?text=${encodeURIComponent(whatsappText)}`;

  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]">
      {/* Mini header */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/90 backdrop-blur-md border-b border-[#DBCEB4]">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">
              k
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px] text-[#2E2A23]">Karina Negrín</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                Pre-consulta de lactancia
              </span>
            </span>
          </Link>
          <button
            onClick={restartForm}
            className="text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
          >
            Empezar de nuevo
          </button>
        </div>
        {/* Progress bar */}
        <div className="h-1 w-full bg-[#E8DDC8]">
          <div
            className="h-full bg-[#8FA08A] transition-[width] duration-700 ease-out"
            style={{ width: `${showSummary ? 100 : progress}%` }}
          />
        </div>
      </header>

      {restoredNotice && (
        <div className="max-w-[820px] mx-auto px-5 pt-4">
          <div className="bg-[#E5EAE0] border border-[#C5D2BB] text-[#3F4D3D] rounded-xl px-4 py-3 text-[13px] flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.7} />
            Recuperamos lo que ya habías escrito antes. Si querés empezar de cero, hacé clic
            arriba en “Empezar de nuevo”.
          </div>
        </div>
      )}

      <main className="max-w-[820px] mx-auto px-5 lg:px-8 py-10 lg:py-16">
        {/* Step counter */}
        {!showSummary && (
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-9 h-px bg-[#8FA08A]" />
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
              {step.type === 'intro' ? 'Antes de empezar' : `Paso ${stepIdx} de ${totalSteps - 1}`}
            </span>
          </div>
        )}

        {/* Step content */}
        {!showSummary && (
          <div key={step.id} className="fade-up">
            <h1 className="font-serif text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.05] tracking-[-0.02em]">
              {step.title}
            </h1>
            {step.subtitle && (
              <p className="font-script text-[#B8806E] text-[24px] sm:text-[28px] mt-2">
                {step.subtitle}
              </p>
            )}

            {step.type === 'intro' && (
              <div className="mt-8 space-y-5 text-[16px] leading-[1.8] text-[#2E2A23]/85 font-light">
                <p>{step.intro}</p>
                {step.intro2 && (
                  <p className="bg-[#E5EAE0] border border-[#C5D2BB] rounded-xl px-5 py-4 text-[15px] text-[#3F4D3D]">
                    {step.intro2}
                  </p>
                )}
                {step.note && (
                  <p className="text-[14px] text-[#8A7E6E] italic border-l-2 border-[#8FA08A] pl-4">
                    {step.note}
                  </p>
                )}
              </div>
            )}

            {step.fields && (
              <div className="mt-9 space-y-7">
                {step.fields.map((f) => (
                  <FieldRow key={f.key} field={f} value={answers[f.key] || ''} onChange={(v) => setField(f.key, v)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        {showSummary && (
          <div className="fade-up">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 size={18} strokeWidth={1.6} className="text-[#8FA08A]" />
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
                Resumen final
              </span>
            </div>
            <h1 className="font-serif text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.05] tracking-[-0.02em]">
              ¡Gracias por tomarte este tiempo!
            </h1>
            <p className="font-script text-[#B8806E] text-[26px] mt-2">Te leí con cuidado</p>
            <p className="mt-6 text-[16.5px] leading-[1.8] text-[#2E2A23]/85 font-light max-w-[620px]">
              Revisá lo que escribiste y, cuando estés lista, hacé clic en enviar para que me
              llegue a mi WhatsApp. Si querés ajustar algo, podés volver a cualquier paso. ¡Gracias!
            </p>

            <div className="mt-10 space-y-5">
              {lactanciaSteps.slice(1).map((s, i) => (
                <SummaryBlock
                  key={s.id}
                  step={s}
                  answers={answers}
                  onEdit={() => editStep(i + 1)}
                />
              ))}
            </div>

            <div className="mt-12 bg-[#FAF4E8] border border-[#DBCEB4] rounded-2xl p-6 sm:p-8">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[#8A7E6E] mb-3">
                Vista previa del mensaje
              </p>
              <pre className="text-[13px] leading-[1.7] text-[#2E2A23]/80 whitespace-pre-wrap font-sans max-h-[260px] overflow-y-auto bg-[#F4EDE2] border border-[#DBCEB4] rounded-lg p-4">
                {whatsappText}
              </pre>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-6 w-full sm:w-auto justify-center"
              >
                <MessageCircle size={16} strokeWidth={1.6} />
                Enviar a Karina por WhatsApp
              </a>
              <p className="mt-4 text-[12px] text-[#8A7E6E] leading-[1.6]">
                Al hacer clic se abre WhatsApp con el mensaje completo. Solo tenés que apretar
                enviar 🌿
              </p>
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-14 pt-8 border-t border-[#DBCEB4] flex items-center justify-between gap-4 flex-wrap">
          <button
            onClick={goPrev}
            disabled={stepIdx === 0 && !showSummary}
            className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={16} strokeWidth={1.6} />
            Atrás
          </button>

          {!showSummary && (
            <button
              onClick={goNext}
              disabled={!validateStep()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {stepIdx === totalSteps - 1 ? 'Ver resumen' : 'Siguiente'}
              <ArrowRight size={16} strokeWidth={1.6} />
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

// --- Subcomponentes ---

const FieldRow = ({ field, value, onChange }) => {
  return (
    <div>
      <label className="block">
        <span className="font-serif text-[20px] sm:text-[22px] text-[#2E2A23] leading-snug">
          {field.label}
          {field.required && <span className="text-[#B8806E] ml-1">*</span>}
        </span>
        {field.helper && (
          <span className="block mt-1.5 text-[14px] text-[#8A7E6E] font-light">{field.helper}</span>
        )}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="mt-3 w-full bg-transparent border-b border-[#C5B496] focus:border-[#B8806E] outline-none py-2.5 text-[16px] text-[#2E2A23] placeholder:text-[#8A7E6E]/50 transition-colors"
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="mt-3 w-full bg-[#FAF4E8] border border-[#DBCEB4] focus:border-[#B8806E] outline-none rounded-xl px-4 py-3 text-[15.5px] leading-[1.6] text-[#2E2A23] placeholder:text-[#8A7E6E]/50 resize-none transition-colors"
        />
      )}

      {field.type === 'radio' && (
        <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
          {field.options.map((opt) => {
            const checked = value === opt;
            return (
              <button
                type="button"
                key={opt}
                onClick={() => onChange(opt)}
                className={`text-left px-4 py-3 rounded-xl border text-[14.5px] transition-all duration-300 ${
                  checked
                    ? 'bg-[#B8806E] text-[#FAF4E8] border-[#B8806E] shadow-sm'
                    : 'bg-[#FAF4E8] text-[#2E2A23] border-[#DBCEB4] hover:border-[#B8806E]/60'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className={`w-3 h-3 rounded-full shrink-0 transition-colors ${
                      checked ? 'bg-[#FAF4E8]' : 'bg-transparent border border-[#C5B496]'
                    }`}
                  />
                  {opt}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SummaryBlock = ({ step, answers, onEdit }) => {
  const filled = step.fields?.filter((f) => (answers[f.key] || '').trim());
  if (!filled || filled.length === 0) return null;

  return (
    <div className="bg-[#FAF4E8] border border-[#DBCEB4] rounded-2xl p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-serif text-[20px] text-[#2E2A23] leading-tight">{step.title}</h3>
          <p className="text-[12px] tracking-[0.18em] uppercase text-[#8A7E6E] mt-1">
            {step.subtitle}
          </p>
        </div>
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.14em] uppercase text-[#B8806E] hover:text-[#9C6856] transition-colors"
        >
          <Pencil size={12} strokeWidth={1.7} />
          Editar
        </button>
      </div>
      <dl className="space-y-2.5">
        {filled.map((f) => (
          <div key={f.key} className="text-[14.5px] leading-[1.6]">
            <dt className="text-[#8A7E6E] text-[13px]">{f.label}</dt>
            <dd className="text-[#2E2A23]/90 mt-0.5">{answers[f.key]}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default LactanciaPreConsulta;
